import React, { Component } from 'react';
import { View, ScrollView, RefreshControl, StyleSheet } from 'react-native';
import { observer } from 'mobx-react';

import Text from '../shared/Text';
import IconButton from '../shared/IconButton';
import Icon from '../shared/Icon';
import Image from '../shared/Image';

import appStore from '../../stores/app';
import heroStore, { putInCombat } from '../../stores/hero';
import islandStore from '../../stores/island';
import warriorInfoModalStore from '../common/WarriorInfoModal/store';

import { t, action } from '../../utils/appUtils';
import { IS_WEB } from '../../lib/appConstants';
import { getWarrior } from '../../lib/dbCalls';
import { validateOpponentForCombat, validateForCombat } from '../../utils/combatUtils';
import { inCombat, isAdmin, isOnline } from '../../utils/warriorUtils';

import { BASE_COLOR, MARGIN_5, PADDING1, pOrT, TOUCHABLE_4 } from '../styleConstants';

import CombatsModal, { joinCombat } from './CombatsModal';

import logImport from '../../lib/log';
const log = logImport as (name: string, params?: Record<string, any>) => void;

const styles = StyleSheet.create({
  container: {
    width: pOrT(170, 218),

    padding: PADDING1,
    height: pOrT(200, 300),
  },
  filterIcon: {
    position: 'absolute',
    right: PADDING1,
    top: PADDING1,
  },
  refreshIcon: {
    position: 'absolute',
    right: 25 + PADDING1,
    top: PADDING1,
  },
  combatsIcon: {
    position: 'absolute',
    right: 25 + PADDING1 + (IS_WEB ? 25 : 0),
    top: PADDING1,
  },
  actionButton: {},
});

type State = {
  showCombatsModal: boolean;
  filter: boolean;
};

@observer
export default class WarriorsInfo extends Component<{}, State> {
  state = {
    showCombatsModal: false,
    filter: false,
  };

  componentDidMount() {
    // TODO: Hack to prevent freezing on first load
    islandStore.warriorsFetching = true;
    setTimeout(async () => {
      await islandStore.fetch(undefined, undefined, true);
    }, 100);
  }

  onCombat = async (id: string) => {
    let validate = validateForCombat(heroStore.hero, t);
    if (validate) {
      appStore.showMessage({ type: 'error', text: validate });
      return;
    }

    appStore.toggleLoading(true);
    const warrior = (await getWarrior(id, appStore.initData))!;

    validate = validateOpponentForCombat(warrior, t, heroStore.hero);
    if (validate) {
      appStore.toggleLoading(false);
      appStore.showMessage({ type: 'error', text: validate });
      islandStore.fetch();
      return;
    }

    if (!inCombat(warrior)) {
      log('attackAction', {
        warriorLogin: heroStore.hero.login,
        opponentWarriorId: id,
      });
      await putInCombat(id);
    } else {
      const result = await joinCombat(warrior.combat!, { warriorId: warrior.id }, true);
      if (typeof result === 'string') {
        appStore.toggleLoading(false);
        appStore.showMessage({
          type: 'error',
          text: result,
        });
      }
    }
  };

  onToggleCombatsModal = () => {
    const { showCombatsModal } = this.state;
    this.setState({
      showCombatsModal: !showCombatsModal,
    });
  };

  render() {
    const { showCombatsModal, filter } = this.state;

    return (
      <View
        style={{
          position: 'absolute',
          right: 0,
          top: -1,
        }}
      >
        <Image
          style={{
            width: pOrT(170, 218),
            height: pOrT(200, 300),
            position: 'absolute',
          }}
          resizeMode="stretch"
          source={require('../../assets/images/paper2.png')}
        />
        <View style={styles.container}>
          <Text bold style={{ fontSize: pOrT(12, 16), marginBottom: MARGIN_5 }}>
            {t('island.onLocation')}
          </Text>
          {!islandStore.warriorsFetching && !islandStore.warriors.length && (
            <Text bold>{t('island.nobodyHere')}</Text>
          )}
          <IconButton
            style={styles.combatsIcon}
            size={18}
            name="combats"
            iconStyle={{
              width: pOrT(20, 24),
              height: pOrT(20, 24),
            }}
            source={require('../../assets/images/combats.png')}
            title={t('combat.combats')}
            onPress={this.onToggleCombatsModal}
            hitSlop={{
              right: TOUCHABLE_4,
              bottom: 10,
            }}
          />
          {IS_WEB ? (
            <IconButton
              onPress={() => {
                islandStore.fetch();
              }}
              source={require('../../assets/images/refresh.png')}
              iconStyle={{ width: 26, height: 26 }}
              style={styles.refreshIcon}
              size={14}
              title={t('refresh')}
              name="refresh"
            />
          ) : null}
          <IconButton
            onPress={() => {
              this.setState({
                filter: !filter,
              });
            }}
            title={t('filter')}
            style={styles.filterIcon}
            hitSlop={
              IS_WEB
                ? {}
                : {
                    bottom: 12,
                    left: TOUCHABLE_4,
                  }
            }
            size={14}
            name={filter ? 'filterFull' : 'filter'}
            iconStyle={{ width: pOrT(18, 22), height: pOrT(18, 22) }}
            source={
              filter
                ? require('../../assets/images/filterOn.png')
                : require('../../assets/images/filter.png')
            }
          />

          <ScrollView
            refreshControl={
              <RefreshControl
                tintColor={BASE_COLOR}
                refreshing={islandStore.warriorsFetching}
                onRefresh={() => islandStore.fetch()}
              />
            }
          >
            {islandStore.warriors 
              .filter((item: any) => heroStore.hero.id !== item.id)
              .filter((item: any) => item.isBot || isOnline(item))
              .filter((item: any) => !filter || (filter && item.level === heroStore.hero.level))
              .map((warrior: any, index: number) => {
                const canBeAttacked =
                  !validateOpponentForCombat(warrior, t, heroStore.hero) &&
                  (!heroStore.hero.combatBots.warriors.includes(warrior.id) || isAdmin(heroStore.hero));

                return (
                  <View key={warrior.id} style={{ flexDirection: 'row', marginTop: index ? 4 : 0 }}>
                    {!warrior.isBot ? (
                      <Icon
                        name="warriorType"
                        source={require('../../assets/images/hero.png')}
                        style={{
                          marginTop: 1,
                          marginRight: 3,
                          height: pOrT(18, 22),
                          width: pOrT(18, 22),
                        }}
                        size={14}
                      />
                    ) : null}
                    {warrior.isMonster ? (
                      <Icon
                        name="monsterType"
                        source={require('../../assets/images/monster.png')}
                        style={{
                          marginTop: 1,
                          marginRight: 3,
                          height: pOrT(18, 22),
                          width: pOrT(18, 22),
                        }}
                        size={14}
                      />
                    ) : null}
                    <Text
                      style={{ fontSize: pOrT(12, 14) }}
                      bold
                      selectable
                    >{`${warrior.login} [${warrior.level}]`}</Text>
                    <IconButton
                      onPress={() =>
                        action(() => {
                          warriorInfoModalStore.toggle(
                            warrior.isBot ? warrior : warrior.id,
                            undefined,
                            canBeAttacked
                              ? {
                                  onAttack: () => {
                                    this.onCombat(warrior.id);
                                  },
                                }
                              : undefined,
                          );
                        })
                      }
                      style={[styles.actionButton, { marginLeft: 5 }]}
                      hitSlop={{
                        right: 0,
                        top: 0,
                        bottom: 0,
                        left: 18,
                      }}
                      iconStyle={{
                        width: pOrT(14, 16),
                        height: pOrT(14, 16),
                      }}
                      size={14}
                      title={t('hero.info')}
                      name="info"
                      source={require('../../assets/images/info.png')}
                    />
                    {canBeAttacked ? (
                      <IconButton
                        style={[styles.actionButton, { marginLeft: 5, marginTop: -1 }]}
                        onPress={() => this.onCombat(warrior.id)}
                        hitSlop={{
                          left: 0,
                          top: 0,
                          bottom: 0,
                          right: 15,
                        }}
                        iconStyle={{
                          width: pOrT(16, 18),
                          height: pOrT(16, 18),
                        }}
                        size={14}
                        name="fight"
                        source={require('../../assets/images/fight.png')}
                        title={t('combat.attack')}
                      />
                    ) : null}
                  </View>
                );
              })}
          </ScrollView>
          {showCombatsModal ? <CombatsModal onHide={this.onToggleCombatsModal} /> : null}
        </View>
      </View>
    );
  }
}
